import type { NotificationItemDTO } from "../api/notifications"
import { OpenAPI } from "../client"

export type NotificationStreamState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"

type NotificationListener = (item: NotificationItemDTO) => void
type StateListener = (state: NotificationStreamState) => void

class NotificationStreamService {
  private abortController: AbortController | null = null
  private state: NotificationStreamState = "disconnected"
  private notificationListeners = new Set<NotificationListener>()
  private stateListeners = new Set<StateListener>()
  private retryAttempt = 0
  private reconnectTimer: number | null = null
  private shouldReconnect = false
  private connectionGeneration = 0
  private lastStreamSequence = 0
  private activeToken = ""

  public getState(): NotificationStreamState {
    return this.state
  }

  public getLastStreamSequence(): number {
    return this.lastStreamSequence
  }

  public subscribe(listener: NotificationListener): () => void {
    this.notificationListeners.add(listener)
    return () => {
      this.notificationListeners.delete(listener)
    }
  }

  public onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener)
    listener(this.state)
    return () => {
      this.stateListeners.delete(listener)
    }
  }

  public waitUntilConnected(timeoutMs = 10000): Promise<boolean> {
    if (this.state === "connected") return Promise.resolve(true)
    return new Promise((resolve) => {
      let settled = false
      let timer: number | null = null
      let unsubscribe: (() => void) | null = null
      const finish = (connected: boolean) => {
        if (settled) return
        settled = true
        if (timer !== null) window.clearTimeout(timer)
        unsubscribe?.()
        resolve(connected)
      }
      unsubscribe = this.onStateChange((state) => {
        if (state === "connected") finish(true)
      })
      timer = window.setTimeout(() => finish(false), timeoutMs)
    })
  }

  private setState(newState: NotificationStreamState) {
    if (this.state === newState) return
    this.state = newState
    this.stateListeners.forEach((listener) => {
      listener(newState)
    })
  }

  public connect(): void {
    const token = localStorage.getItem("access_token") || ""
    if (token !== this.activeToken) {
      this.connectionGeneration++
      this.abortController?.abort()
      this.abortController = null
      this.lastStreamSequence = 0
      this.retryAttempt = 0
      this.activeToken = token
      this.setState("disconnected")
    }
    if (this.state === "connecting" || this.state === "connected") return
    this.shouldReconnect = true
    this.clearReconnectTimer()
    this.startStream()
  }

  public disconnect(): void {
    this.shouldReconnect = false
    this.connectionGeneration++
    this.clearReconnectTimer()
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.lastStreamSequence = 0
    this.retryAttempt = 0
    this.activeToken = ""
    this.setState("disconnected")
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private async startStream() {
    const generation = ++this.connectionGeneration
    this.setState(generation === 1 ? "connecting" : "reconnecting")

    const token = localStorage.getItem("access_token") || ""
    if (!token) {
      this.setState("disconnected")
      return
    }

    this.abortController = new AbortController()
    const baseUrl = OpenAPI.BASE || "/api"
    const cursor =
      this.lastStreamSequence > 0
        ? `?after_sequence=${this.lastStreamSequence}`
        : ""
    const streamUrl = `${baseUrl.replace(/\/$/, "")}/v1/notifications/stream${cursor}`

    try {
      const response = await fetch(streamUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
          "Cache-Control": "no-cache",
        },
        signal: this.abortController.signal,
      })

      if (generation !== this.connectionGeneration) {
        return
      }

      if (!response.ok || !response.body) {
        throw new Error(`SSE HTTP error ${response.status}`)
      }

      this.retryAttempt = 0
      this.setState("connected")

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""
      let currentEvent = ""
      let currentID = ""
      const currentData: string[] = []

      while (generation === this.connectionGeneration) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        // Keep unfinished line in buffer
        buffer = lines.pop() ?? ""

        for (const rawLine of lines) {
          const line = rawLine.trimRight()
          if (line.startsWith("event:")) {
            currentEvent = line.substring(6).trim()
          } else if (line.startsWith("id:")) {
            currentID = line.substring(3).trim()
          } else if (line.startsWith("data:")) {
            currentData.push(line.substring(5).trimLeft())
          } else if (line === "") {
            if (currentEvent && currentData.length > 0) {
              this.handleEvent(currentEvent, currentData.join("\n"), currentID)
            }
            currentEvent = ""
            currentID = ""
            currentData.length = 0
          }
        }
      }

      // Stream closed normally
      this.handleStreamEnded(generation)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") {
        return
      }
      if (generation === this.connectionGeneration) {
        this.handleStreamEnded(generation)
      }
    }
  }

  private handleEvent(event: string, data: string, eventID = "") {
    if (event === "notification" && data) {
      try {
        const item: NotificationItemDTO = JSON.parse(data)
        const sequenceFromID = Number(eventID)
        if (Number.isFinite(sequenceFromID) && sequenceFromID > 0) {
          this.lastStreamSequence = Math.max(
            this.lastStreamSequence,
            sequenceFromID,
          )
        }
        if (typeof item.stream_sequence === "number") {
          this.lastStreamSequence = Math.max(
            this.lastStreamSequence,
            item.stream_sequence,
          )
        }
        this.notificationListeners.forEach((listener) => {
          listener(item)
        })
      } catch {
        // Ignored invalid JSON payload
      }
    }
  }

  private handleStreamEnded(generation: number) {
    if (generation !== this.connectionGeneration || !this.shouldReconnect) {
      return
    }

    this.abortController = null
    this.setState("reconnecting")

    const delay = Math.min(3000 * 2 ** Math.min(this.retryAttempt, 3), 30000)
    this.retryAttempt++

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      if (this.shouldReconnect) {
        void this.startStream()
      }
    }, delay)
  }
}

export const notificationStreamService = new NotificationStreamService()
